import { getDefaultExtent } from "@omnia/shared/map/map";
import { IOmniaSettingsKey } from "@omnia/types";
import { FieldViewModel } from "../../fields";
import { OmniaModuleId, RegionId } from "../../generated/enums";
import { convertGeometryToOpenLayers } from "../../geom";
import { createGeometryCache } from "../../geom/cache";
import { BasemapGroup } from "../../map/basemaps/group";
import { IEditMap } from "../../map/edit/types";
import { getExtentForLayers, Layer } from "../../map/layer";
import { MapContainer } from "../../map/mapContainer";
import { IBasemapGroup, ILayer } from "../../map/types";
import { RoleId } from "../../user";
import { selectMany } from "../../utils/array";
import { isArray, isObject, isString, isUndefinedOrNull } from "../../utils/utils";
import { FieldsMapFilter, IFieldsMap, IFieldsMapContainerContext, IFieldsMapContainerOptions } from "../map";
import { SharedFieldsService } from "../service";
import { FieldsMapLabels } from "../types";
import { watchFields } from "../watch";
import { getCroppingFilter } from "./filter";
import { CroppingLayer } from "./layers/croppingLayer";
import { FieldsLayer } from "./layers/fieldsLayer";
import { createScotlandPSorpBasemap } from "./layers/scotlandPSorpBasemap";
import { createUKSOBasemaps } from "./layers/uksoBasemaps";
import { ol_Extent, ol_extent, ol_Observable_unByKey, ol_geom } from "@omnia/exports/openlayers";

export class FieldsMap
    extends MapContainer<IFieldsMapContainerContext, IFieldsMapContainerOptions>
    implements IFieldsMap, IEditMap {
    protected _disposeFunctions: Function[] = [];

    private _geometryCache: (key: FieldViewModel) => ol_geom.Geometry;

    protected svc: SharedFieldsService;

    protected _fieldsLayer: FieldsLayer;
    protected _croppingLayer: CroppingLayer;

    private _year: number;

    protected _filters: FieldsMapFilter;

    private _init: ng.IPromise<any>;

    constructor(ctxt: IFieldsMapContainerContext, options?: IFieldsMapContainerOptions) {
        super();

        this.svc = ctxt.fieldSvc;

        this.options = options = options || {
            viewDefaultExtent: true,
            applyFilters: true,
        };

        // 241112 - DevOps #2862
        // The default behaviour when not passing "options" was to have be properties below to true
        // Options seem to be more frequently provided now, but some do not seem to mention these properties,
        // so they are set again has defaults here
        options.applyFilters ??= true
        options.viewDefaultExtent ??= true

        this._geometryCache = this.options.cacheGeometries
            ? createGeometryCache<FieldViewModel>((f) => f.getBoundary())
            : undefined;

        const mapSettings = this.svc.getSettings("map");

        this.createLayers(ctxt, options);

        super.setup(ctxt, options, mapSettings);

        let viewDefaultExtent = isUndefinedOrNull(options.viewDefaultExtent) ? true : options.viewDefaultExtent;
        this.setupFieldsWatcher(viewDefaultExtent);
    }

    dispose() {
        const d = this._disposeFunctions;
        this._disposeFunctions = [];
        if (d) {
            d.forEach((fn) => fn());
        }

        super.dispose();
    }

    getInitPromise() {
        return this._init;
    }

    protected getModuleMapSettings() {
        const sh = this.ctxt.shell;
        const session = sh.getSession();
        const moduleId = OmniaModuleId[sh.getCurrentModule().moduleId];
        const ns = `${moduleId}.map`;
        return session.getSettings(ns);
    }

    private createLayers(ctxt: IFieldsMapContainerContext, options?: IFieldsMapContainerOptions) {
        // Create the cropping layer first (since this checks user preferences)
        this._croppingLayer = new CroppingLayer(
            {
                visible: true,
                showLabels: true,
                ...options?.layers?.croppings,
            },
            ctxt.fieldSvc.getSession()
        );

        // Create the cropping layer second (so we can respect user preferences)
        this._fieldsLayer = new FieldsLayer({
            visible: true,
            // This property is synced with, and so should inherit initial value from, the cropping layer's setting.
            showLabels: this._croppingLayer.showFieldNames || !this._croppingLayer.visible,
            ...options?.layers?.fields,
        });
    }

    private getLayerSettings(ctxt: IFieldsMapContainerContext, layerKey: string): IOmniaSettingsKey {
        if (!ctxt) throw new Error("`ctxt` is undefined");
        const session = ctxt.shell.getSession();
        const namespace = session.getSettings("map").getSettings("layers");

        return namespace.getKey(layerKey);
    }

    protected setupBasemaps() {
        const basemaps = super.setupBasemaps();

        const targetLayers = this._map.customBasemapsGroup.getLayers();

        const ukso = createUKSOBasemaps(targetLayers, this.settings);

        const psorp = createScotlandPSorpBasemap(targetLayers, this.settings);

        return (basemaps ?? []).concat([ukso, psorp]);
    }

    isBasemapAvailable(group: BasemapGroup) {
        if (group?.key === "scotlandPSorp") {
            return this.svc.holding?.Region === RegionId.Scotland;
        }

        return true;
    }

    private setupFieldsWatcher(viewDefaultExtent: boolean) {
        const { showDisabledFields } = this.options;

        const watcher = () => {
            this.resetFields(viewDefaultExtent);
            viewDefaultExtent = false;
        };

        this._init = watchFields(this.svc, this.ctxt.$rootScope, watcher, showDisabledFields).then((unreg) => {
            this._disposeFunctions.push(unreg);

            this._init = undefined;
        });
    }

    private resetFields(viewDefaultExtent: boolean) {
        if (!this._map) return;

        this.setFields();

        if (viewDefaultExtent) {
            this._map.viewDefaultExtent();
        }
    }

    get year() {
        return this._year || this.svc.yearId;
    }

    showYear(year: number) {
        this._year = year;
        this.resetFields(false);
    }

    get fieldsLayer() {
        return this._fieldsLayer;
    }

    get croppingLayer() {
        return this._croppingLayer;
    }

    get visibleLabels() {
        const fld = this._fieldsLayer;
        const crop = this._croppingLayer;

        return (
            (fld.showLabels ? FieldsMapLabels.field : 0) |
            (crop.showLabels && crop.showFieldNames ? FieldsMapLabels.field : 0) |
            (crop.showLabels && crop.showCropNames ? FieldsMapLabels.crop : 0) |
            (crop.showLabels && crop.showVarietyNames ? FieldsMapLabels.variety : 0)
        );
    }

    get applyFilters() {
        return this.options.applyFilters;
    }

    set applyFilters(value) {
        if (value != this.options.applyFilters) {
            this.options.applyFilters = value;
            this.setFields();
        }
    }

    get filters() {
        return this._filters;
    }

    set filters(value) {
        this._filters = value;
        this.setFields();
    }

    protected setupLayers() {
        if (!this._map) return;

        const l = (this.layers = <Layer[]>[this._fieldsLayer, this._croppingLayer]);

        this._map.layers = l;

        this._select.setLayers(l, this.synchronizeSelectionFromMap);

        this._edit.setSnappingLayers(l);

        this.setupLayerWatcher();
    }

    getDefaultExtent() {
        let extent: ol_Extent;

        if (this.fieldsLayer) {
            extent = getExtentForLayers([this.fieldsLayer]);
            extent = extent ? ol_extent.buffer(extent, 100) : extent;
        }

        if (!extent) {
            var holding = this.svc.holding;

            if (holding && holding.Town && holding.Town.Boundary) {
                const geom = convertGeometryToOpenLayers(holding.Town.Boundary);
                if (geom && geom.getExtent) extent = geom.getExtent();
            }
        }

        if (!extent) {
            extent = getDefaultExtent();
        }

        return extent;
    }

    protected setFields() {
        const { fields, cropping } = this.getVisibleFields();
        const { year, _geometryCache: cache } = this;

        this._fieldsLayer.setFields(fields, cache);
        this._croppingLayer.setCrops(cropping, year, cache);
    }

    protected getVisibleFields() {
        const filters = (this.applyFilters ? this._filters : undefined) || this.getFilters();

        // 230228 - Include disabled fields if their boundary is not ignored
        // Those are supposed to be visible on the map, albeit muted
        const fields = this.svc
            .getFields(true)
            .filter((f) => !f.disabled || !f.ignoreBoundary)
            .filter(filters.fields);

        const cropping = selectMany(fields.map((f) => f.croppings.filter(filters.cropping)));

        return { fields, cropping };
    }

    protected getFilters(): FieldsMapFilter {
        const f = isUndefinedOrNull(this.options.applyFilters) ? true : this.options.applyFilters;

        return {
            fields: f ? (field: FieldViewModel) => field && field.visible : (field: FieldViewModel) => !!field,

            cropping: getCroppingFilter(this.year, f),
        };
    }

    protected updateSelectionFromMap(features: any[]) {
        if (this.synchronizeSelectionFromMap && isArray(features)) {
            this.synchronizeSelection(() => {
                var selectedItems = Layer.getDataFromFeatures<FieldViewModel>(features);

                this.svc.selectItems(selectedItems, true);
            });
        }
    }

    showFeaturesWithData(items: any[], select: boolean) {
        var features = this.findFeaturesWithData(items);

        // Select the items.
        if (select && features.length === items.length) {
            this.svc.selectItems(items, true);
            this.selectFeatures(features);
        }

        // Focus the map.
        return this.viewExtentForDataItems(items);
    }

    updateMapSelection() {
        this.synchronizeSelection(() => {
            var selectedItems = this.svc.selectedItems;

            var features = this.findFeaturesWithData(selectedItems);

            this._select.selectFeatures(features);
        });
    }

    protected setupLayerWatcher() {
        const layers = [this._fieldsLayer, this._croppingLayer];

        layers.forEach((l) => {
            const key = l.mapLayer.on("change:visible", () => this.onLayerStateChanged(l));
            this._disposeFunctions.push(() => ol_Observable_unByKey(key));
        });

        this._disposeFunctions.push(
            this.ctxt.$rootScope.$watch(
                () => this._croppingLayer.selectable,
                () => this.onLayerStateChanged(this._croppingLayer, "cropping")
            ),
            this.ctxt.$rootScope.$watch(
                () => this._croppingLayer.showFieldNames,
                () => this.onLayerStateChanged(this._croppingLayer, "cropping")
            ),

            this.ctxt.$rootScope.$watch(
                () => this._fieldsLayer.showLabels,
                () => this.onLayerStateChanged(this._fieldsLayer, "fields")
            ),

            // Watch for changes to each layer's settings.
            this.ctxt.$rootScope.$watchGroup(
                [() => this._fieldsLayer.visible, () => this._fieldsLayer.showLabels],
                ([visible, showLabels]) => {
                    this.getLayerSettings(this.ctxt, FieldsLayer.LOCAL_STORAGE_KEY).set({
                        visible,
                        showLabels,
                    });
                }
            ),
            this.ctxt.$rootScope.$watchGroup(
                [
                    () => this._croppingLayer.visible,
                    () => this._croppingLayer.showLabels,
                    () => this._croppingLayer.croppingStyleMode,
                    () => this._croppingLayer.croppingLabels,
                ],
                ([visible, showLabels, croppingStyleMode, croppingLabels]) => {
                    this.getLayerSettings(this.ctxt, CroppingLayer.LOCAL_STORAGE_KEY).set({
                        visible,
                        showLabels,
                        croppingStyleMode,
                        croppingLabels,
                    });
                }
            )
        );

        this.onLayerStateChanged();
    }

    /**
     * This is called internally when the state of a watched layer changes
     * @param layer Layer
     */
    private onLayerStateChanged(layer?: Layer, type?: string) {
        // If the "croppings" layer is visible, then the labels on that layer should take precedence.
        this._fieldsLayer.showFieldNames = !this._croppingLayer.isVisible;

        // This property exists twice, on "fields" and on "croppings". Sync it.
        if (type === "cropping") {
            this._fieldsLayer.showLabels = this._croppingLayer.showFieldNames;
        } else {
            this._croppingLayer.showFieldNames = this.fieldsLayer.showLabels;
        }

        this._fieldsLayer.selectable =
            (this.options.canSelectFields && !this._croppingLayer.isVisible) || !this._croppingLayer.selectable;

        // 241014 - The previous version would reset the selection each time the visibility of labels changes,
        // which does not make any sense in retrospect
        // if (layer && ((!layer.isVisible) || (!layer.showLabels))) {
        if (layer && !layer.isVisible) {
            var features = this.getSelectedFeatures();

            if (features && features.some((f) => f.get("layer") == layer)) {
                this.clearSelection();
            }
        }
    }

    /**
     * This is called externally when the visibility of a layer or basemap is changed by the user,
     * from the "map options" popup
     *
     * Internal visibility changes (i.e., the .visible property being changed in code) will not cause
     * this method to be called
     *
     * @param target Target of the change - it can be either a layer or a group of basemaps
     */
    onVisibilityChanged(target: ILayer | IBasemapGroup) {
        function isBasemapGroup(a: any): a is IBasemapGroup {
            return a && isString(a["key"]) && isObject(a["basemap"]) && isArray(a["basemaps"]);
        }

        if (isBasemapGroup(target)) {
            if (target.key == "ukso") {
                this.croppingLayer.visible = !target.visible;
            }
        }
    }

    canSetLabelOptions() {
        const session = this.svc ? this.svc.getSession() : null;
        return session && session.currentUserRole <= RoleId.Admin;
    }
}
